import { z } from 'zod';
import { DealBucketSchema } from './account-hierarchy-deals';

/**
 * Wire schema for `GET /api/flow/accounts/:accountId/hierarchy` — the
 * Hierarchy tab on the account drilldown (Table + Graph).
 *
 * The response is a FLAT `nodes[]` with an effective `parent_id` already
 * resolved server-side (`resolveHierarchyParents` in
 * `account-hierarchy-tree.ts`); the client builds the tree from it. Metric
 * columns are the account mirror's own values — roll-ups across a subtree are
 * computed client-side, never fabricated server-side.
 *
 * Schema is the source of truth; TS types are `z.infer` only.
 */

/** Hard cap on nodes returned for one hierarchy (query asks for cap + 1 as a truncation sentinel). */
export const HIERARCHY_NODE_CAP = 500;
/**
 * Deepest level returned (the root is level 0). The upward walk stops here, and
 * the downward walk recurses one level further only as a sentinel to detect
 * (and report via `truncated`) a tree that is deeper than this. The upward
 * walk reports a cut the same way (the top node it reached still has a parent).
 */
export const HIERARCHY_MAX_DEPTH = 20;
/** A parent with more children than this shows the first N plus a "+N accounts" node. */
export const HIERARCHY_OVERFLOW_CHILDREN = 12;

export const AccountHierarchyNodeSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  /** Effective parent in THIS response's node set; null only for the root. */
  parent_id: z.string().nullable(),
  /** The raw SF `ParentId` as mirrored (may point outside the node set). */
  sf_parent_account_id: z.string().nullable(),
  ultimate_global_parent_id: z.string().nullable(),
  /** Traction hierarchy depth — display/tiebreak only, never builds the tree. */
  hierarchy_level: z.number().nullable(),
  /** True when the account has a parent (or a re-attached cycle) that is not in the node set. */
  parent_missing: z.boolean(),
  owner_name: z.string().nullable(),
  account_type: z.string().nullable(),
  region: z.string().nullable(),
  number_of_employees: z.number().nullable(),
  arr_active_subscriptions: z.number().nullable(),
  open_pipeline: z.number().nullable(),
  fortune_500_rank: z.number().nullable(),
  contact_count: z.number(),
  /**
   * `Separate_Buying_Center__c`: this child buys separately from its parent. NULL in the
   * mirror (never set in SF) is coalesced to false in SQL, so the wire value is a plain boolean.
   */
  separate_buying_center: z.boolean(),
  /** One-sentence reason behind the flag (`Buying_Center_Summary__c`); only sent for flagged accounts. */
  buying_center_summary: z.string().nullable(),
  /** `Enterprise_Renewal_Date__c` (end of the current Enterprise contract) as `YYYY-MM-DD`. */
  enterprise_renewal_date: z.string().nullable(),
  /** `Health_Rating__c`: Green / Yellow / Red (free text in the mirror, so a string). */
  health_rating: z.string().nullable(),
  /** `Health_Red_Reason__c` multiselect, already split. */
  health_red_reason: z.array(z.string()),
  /** `Renewal_Risk_Reasons__c` multiselect, already split. */
  renewal_risk_reasons: z.array(z.string()),
  /** `Contracted_ARR_Revenue_Model__c`, USD. */
  contracted_arr: z.number().nullable(),
  /** `Upcoming_Renewal__c`: the Salesforce id of the account's upcoming renewal opportunity. */
  upcoming_renewal_opp_id: z.string().nullable(),
});
export type AccountHierarchyNode = z.infer<typeof AccountHierarchyNodeSchema>;

export const AccountHierarchyResponseSchema = z.object({
  root_id: z.string(),
  current_id: z.string(),
  nodes: z.array(AccountHierarchyNodeSchema),
  /** True when `HIERARCHY_NODE_CAP` or `HIERARCHY_MAX_DEPTH` cut the tree. */
  truncated: z.boolean(),
  /**
   * Aggregate deal buckets (account x status x pipeline x type x close month) for the deal
   * overlay; the client applies the filters. Read from `salesforce_opportunity`, USD.
   */
  deal_buckets: z.array(DealBucketSchema),
  /** True when more than `DEAL_BUCKET_CAP` buckets exist (the overlay's totals are partial). */
  deals_truncated: z.boolean(),
});
export type AccountHierarchyResponse = z.infer<typeof AccountHierarchyResponseSchema>;
